import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast } from "@shared/hooks/use-toast";
import { CXCxOnboardingApi } from "./api";
import {
  ActivateConsumerPayload,
  CreateConsumerNotePayload,
  UpdateConsumerApprovalPayload,
  UpdateConsumerDocumentPayload,
  UpdateConsumerInstallationPayload,
  UpdateConsumerServicePayload,
  UpdateMeterInstallationPayload,
} from "./types";
import { consumerApi } from "../consumerManagement/api";
import { queryClient } from "@shared/api/queries/queryClient";
import { logEvent } from "@shared/analytics/analytics";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { downloadCSV } from "@shared/utils/utils";

// Hook for setup progress (original)
export const useConsumerSummary = (params: {
  remoteUtilityId: any;
  activeTab: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.consumerSummary(params),
    () => CXCxOnboardingApi.getSummary(params),
  );
};

export const useConsumerList = (params: {
  remoteUtilityId: any;
  page: number;
  limit: number;
  searchData: string;
  status: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.consumerList(params),
    () => CXCxOnboardingApi.getConsumerList(params),
  );
};

export const useConsumerOnboardingExport = (params: {
  remoteUtilityId: any;
  page: number;
  limit: number;
  searchData: string;
  status: string;
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.consumerExport(params),
    async () => {
      setIsExporting(true);
      try {
        const blob = await CXCxOnboardingApi.exportConsumerList(params); // Adjust API object name if needed
        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });

        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export consumer onboarding list: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    },
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

export const useConsumerListHistory = (params: {
  remoteUtilityId: any;
  page: number;
  limit: number;
  searchData: string;
  status: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.consumerListHistory(params),
    () => CXCxOnboardingApi.getConsumerListHistory(params),
    {
      enabled: !!params.remoteUtilityId,
    },
  );
};
export const useConsumerDetails = (params: {
  remote_utility_id: any;
  consumer_id: string;
  is_detail_info: boolean;
}) => {
  const enabled =
    !!params.remote_utility_id &&
    !!params.consumer_id &&
    typeof params.is_detail_info === "boolean";

  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.consumerDetails(params),
    () => CXCxOnboardingApi.getDetails(params),
    {
      enabled: enabled,
    },
  );
};
export const useConsumerDetailsforPayment = (params: {
  remote_utility_id: any;
  consumer_id: string;
  is_detail_info: boolean;
}) => {
  const enabled =
    !!params.remote_utility_id &&
    !!params.consumer_id &&
    typeof params.is_detail_info === "boolean";

  return useSmartQuery(
    QueryKeyFactory.module.cx.nsc.consumerDetails(params),
    () => CXCxOnboardingApi.getDetails(params),
    {
      enabled: !enabled,
    },
  );
};

export const useConsumerOnboardingHistoryExport = (params: {
  remoteUtilityId: any;
  page: number;
  limit: number;
  searchData: string;
  status: string;
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportMutation = useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.consumerListHistory(params),
    async () => {
      setIsExporting(true);
      try {
        const blob = await CXCxOnboardingApi.exportConsumerListHistory(params);
        toast({
          title: "Export Initiated",
          description: blob?.message,
          variant: "default",
        });

        return { success: true };
      } catch (error: any) {
        toast({
          title: `Failed to export consumer onboarding history: ${error.message}`,
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsExporting(false);
      }
    },
    {
      enabled: false,
    },
  );

  const exportCSV = () => {
    exportMutation.refetch();
  };

  return {
    exportCSV,
    isExporting: isExporting || exportMutation.isFetching,
    error: exportMutation.error,
    isError: exportMutation.isError,
  };
};

export const useDocument = (params: {
  remote_utility_id: any;
  consumer_id: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.consumerDocument(params),
    () => CXCxOnboardingApi.getDocument(params),
  );
};

export const useServiceOrder = (params: {
  remote_utility_id: any;
  search_data: string;
  utility_service: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.serviceOrder(params),
    () => CXCxOnboardingApi.getServiceOrder(params),
  );
};

export const useSiteInspectionServiceOrder = (params: {
  remote_utility_id: any;
  search_data: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.siteInspectionServiceOrder(params),
    () => CXCxOnboardingApi.getSiteInspectionServiceOrder(params),
  );
};

export const useServiceOrderByConsumer = (params: {
  remote_utility_id: any;
  consumer_id: string;
  tab_name: string;
}) => {
  console.log("🔍 useServiceOrderByConsumer called with params:", params);
  console.log("🔍 Parameter validation:", {
    remote_utility_id: !!params.remote_utility_id,
    consumer_id: !!params.consumer_id,
    tab_name: !!params.tab_name,
    allValid:
      !!params.remote_utility_id && !!params.consumer_id && !!params.tab_name,
  });

  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.serviceOrderByConsumer(params),
    () => {
      console.log(
        "🚀 useServiceOrderByConsumer API call executing with params:",
        params,
      );
      return CXCxOnboardingApi.getServiceOrderByConsumer(params);
    },
    {
      enabled:
        !!params.remote_utility_id && !!params.consumer_id && !!params.tab_name,
    },
  );
};

export const useUpdateConsumerService = () => {
  return useSmartMutation(
    ({
      consumerId,
      payload,
    }: {
      consumerId: string;
      payload: UpdateConsumerServicePayload;
    }) => CXCxOnboardingApi.updateConsumerService(consumerId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "serviceOrderConsumerforInstallation"],
        });
      },
      onError: (error: Error) => {
        logEvent("Error: WorkFlow Step Site Inspection", {
          error: error.message,
        });
      },
    },
  );
};
export const useUpdateMeterInstallation = () => {
  return useSmartMutation(
    ({
      consumerId,
      payload,
    }: {
      consumerId: string;
      payload: UpdateMeterInstallationPayload;
    }) => CXCxOnboardingApi.updateMeterInstallation(consumerId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "serviceOrderConsumerforInstallation"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "connectionDetail"],
        });
      },
    },
  );
};
export const useUpdateConsumerDocument = () => {
  return useSmartMutation(
    ({
      document_id,
      payload,
    }: {
      document_id: any;
      payload: UpdateConsumerDocumentPayload;
    }) => CXCxOnboardingApi.updateConsumerDocument(document_id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerDocument"],
        });
        queryClient.invalidateQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
      },
    },
  );
};

export const useUpdateConsumerApprovalStatus = () => {
  return useSmartMutation(
    ({
      consumerId,
      payload,
    }: {
      consumerId: string;
      payload: UpdateConsumerApprovalPayload;
    }) => CXCxOnboardingApi.updateConsumerApprovalStatus(consumerId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
      },
    },
  );
};

export const useUpdateConsumerStatus = () => {
  return useSmartMutation(
    ({ consumerId, status }: { consumerId: string; status: string }) =>
      CXCxOnboardingApi.updateConsumerStatus(consumerId, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerDocument"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
      },
      onError: (error: Error) => {
        logEvent("Error: WorkFlow Step Application review", {
          error: error.message,
        });
      },
    },
  );
};

export const useCreateConsumerNote = () => {
  return useSmartMutation(
    (payload: CreateConsumerNotePayload) =>
      CXCxOnboardingApi.createConsumerNote(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "getNotes"],
        });
      },
    },
  );
};

export const useGetNotes = (params: {
  remote_utility_id: any;
  request_id: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.getNotes(params),
    () => CXCxOnboardingApi.getNotes(params),
  );
};

export const useDownloadReport = () => {
  return useMutation({
    mutationFn: (params: { remote_utility_id: any; report_id: string }) =>
      CXCxOnboardingApi.downloadReport(params),
    onSuccess: (data: Blob, variables) => {
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `inspection-report-${variables.report_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useMasterConsumerSearch = (params: {
  remoteUtilityId: any;
  searchData: string;
  requestType?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.searchMasterConsumer(params),
    () => CXCxOnboardingApi.searchMasterConsumer(params),
    {
      enabled: params.searchData.length >= 3,
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  );
};

export const useSearchMeter = (params: {
  remote_utility_id: any;
  utility_service: any;
  premise: string;
  device_number?: any;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.searchMeter(params),
    () => CXCxOnboardingApi.searchMeter(params),
  );
};

export const useAssignedUnitResources = (
  params: {
    consumer_id?: string;
    remote_utility_id?: any;
    utility_service?: any;
  } | null,
) => {
  return useSmartQuery(
    params
      ? ["cxonboarding", "assignedUnitResources", params]
      : ["cxonboarding", "assignedUnitResources", "disabled"],
    () =>
      params
        ? CXCxOnboardingApi.getAssignedUnitResources(params)
        : Promise.resolve(null),
    {
      enabled:
        !!params &&
        !!params.consumer_id &&
        !!params.remote_utility_id &&
        !!params.utility_service,
    },
  );
};
export const useStatus = (params: {}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.status(),
    () => CXCxOnboardingApi.getStatus(),
    {
      staleTime: 2 * 60 * 1000,
      refetchInterval: 30000,
    },
  );
};
export const useActivateConsumer = () => {
  return useSmartMutation(
    ({
      consumerId,
      payload,
    }: {
      consumerId: string;
      payload: ActivateConsumerPayload;
    }) => CXCxOnboardingApi.activateConsumer(consumerId, payload),
    {
      onError: (error: Error) => {
        logEvent("Error: WorkFlow Step Activation", {
          error: error.message,
        });
      },
    },
  );
};

export const useUpdateConsumerInstallationStatus = () => {
  return useSmartMutation(
    ({ consumerId, payload }: { consumerId: string; payload: any }) =>
      CXCxOnboardingApi.updateConsumerInstallationStatus(consumerId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "serviceOrderConsumer"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "searchMeter"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "quantity-wise", "resource"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerMeter"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "cxonboarding", "consumerMeter"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerDetails"],
        });
      },
      onError: (error: any) => {
        logEvent("Error: WorkFlow Step Installation", {
          error: error?.message || "Error",
        });
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          (typeof error?.response?.data === "string"
            ? error.response.data
            : null) ||
          error?.message ||
          "Failed to update installation status";

        toast({
          title: "Error updating installation status",
          description: errorMessage,
          variant: "destructive",
        });
      },
    },
  );
};

export const useInstallationCompleted = () => {
  return useSmartMutation(
    ({ consumerId, payload }: { consumerId: string; payload: any }) =>
      CXCxOnboardingApi.installationCompleted(consumerId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "quantity-wise", "resource"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerMeter"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "cxonboarding", "consumerMeter"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerDetails"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "connectionDetail"],
        });
      },
    },
  );
};

export const useSkipSiteInspection = () => {
  return useSmartMutation(
    ({ consumerId }: { consumerId: string }) =>
      CXCxOnboardingApi.skipSiteInspection(consumerId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "serviceOrderConsumerforInstallation"],
        });
      },
    },
  );
};

export const useServiceOrderConsumer = (params: {
  remote_utility_id: string;
  consumer_id: string;
  tab_name?: string;
  utility_service?: string;
  job_card_id?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.serviceOrderConsumerforInstallation(
      params,
    ), // Include params in query key for caching
    () => CXCxOnboardingApi.getServiceOrderByConsumer(params),
    {
      enabled: !!params.remote_utility_id && !!params.consumer_id,
    },
  );
};

export const useServiceOrderConsumerforInstallation = (params: {
  remote_utility_id: string;
  consumer_id: string;
  tab_name: string;
  is_enabled: boolean;
  utility_service?: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.serviceOrderConsumer(params), // Include params in query key for caching
    () => CXCxOnboardingApi.getServiceOrderByConsumer(params),
    {
      enabled:
        !!params.remote_utility_id &&
        !!params.consumer_id &&
        !!params.tab_name &&
        params.is_enabled,
    },
  );
};

export const useConsumerMeter = (params: {
  consumer_id: string;
  remote_utility_id: string;
}) => {
  return useSmartQuery(
    QueryKeyFactory.module.cx.cxonboarding.consumerMeter(params), // Include params in query key for caching
    () => CXCxOnboardingApi.getConsumerMeter(params),
    {
      enabled: !!params.consumer_id && !!params.remote_utility_id,
    },
  );
};

export const useUpdateStatus = (consumerId: number) => {
  return useSmartMutation(
    (payload: any) => CXCxOnboardingApi.updateStatus(consumerId, payload),
    {
      onSuccess: async () => {
        queryClient.refetchQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
        queryClient.refetchQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    },
  );
};

export const useReuploadConsumerDocument = () => {
  return useSmartMutation(
    ({ documentId, formData }: { documentId: any; formData: FormData }) =>
      CXCxOnboardingApi.reuploadConsumerDocument(documentId, formData),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ["cxonboarding", "consumerDocument"],
        });
        queryClient.refetchQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
      },
    },
  );
};

export const useUpdateQuantityConnection = () => {
  return useSmartMutation(
    ({
      consumerId,
      remoteUtilityId,
      payload,
    }: {
      consumerId: string;
      remoteUtilityId: any;
      payload: {
        remote_utility_id: any;
        utility_service: string;
        quantity: number;
        action?: string;
      };
    }) =>
      CXCxOnboardingApi.updateQuantityConnection(
        consumerId,
        remoteUtilityId,
        payload,
      ),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ["nsc", "consumerDetails"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cxonboarding", "consumerList"],
        });
        queryClient.invalidateQueries({
          queryKey: ["cx", "quantity-wise", "resource"],
        });
        queryClient.refetchQueries({
          queryKey: ["cx", "connectionDetail"],
        });

        toast({
          title: `${variables?.payload?.utility_service || "Quantity"} quantity added`,
          description: `Quantity for ${variables?.payload?.utility_service || "utility"} service updated (${variables?.payload?.quantity || 1} units).`,
        });
      },
      onError: (error: Error) => {
        logEvent("Error: Update Quantity Connection", {
          error: error.message,
        });

        toast({
          title: "Error updating quantity",
          description:
            error?.message || "Failed to update quantity-based connection.",
          variant: "destructive",
        });
      },
    },
  );
};

export const useConnectionDetail = (params: {
  remote_utility_id: any;
  consumer_id: string;
  tab_name?: string;
}) => {
  return useSmartQuery(
    ["cx", "connectionDetail", params],
    () => CXCxOnboardingApi.getConnectionDetail(params),
    {
      enabled: !!params.consumer_id && !!params.remote_utility_id,
    },
  );
};
